import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { 
  Upload, 
  Files, 
  Activity, 
  TrendingUp, 
  TrendingDown,
  Clock, 
  HardDrive,
  ArrowUpRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatFileSize, formatRelativeTime } from '../utils/formatters';
import { apiClient } from '../api/client';
import { DashboardStats, TrendData } from '../types';

export const DashboardPage = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper function to render trend indicators
  const renderTrendIndicator = (trend: TrendData | undefined, period: string) => {
    if (!trend || trend.percentage === 0) {
      return (
        <div className="mt-4 flex items-center text-sm">
          <span className="text-gray-600">No change from {period}</span>
        </div>
      );
    }

    const isPositive = trend.isPositive;
    const percentage = Math.abs(trend.percentage);
    
    return (
      <div className="mt-4 flex items-center text-sm">
        {isPositive ? (
          <TrendingUp className="w-4 h-4 text-success-500 mr-1" />
        ) : (
          <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
        )}
        <span className={`font-medium ${isPositive ? 'text-success-600' : 'text-red-600'}`}>
          {isPositive ? '+' : '-'}{percentage}%
        </span>
        <span className="text-gray-600 ml-1">from {period}</span>
      </div>
    );
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await apiClient.get('/files/stats/basic');
        if (response.data.success) {
          const data = response.data.data;
          setStats({
            totalFiles: data.totalFiles,
            totalSize: data.totalSize,
            totalSizeFormatted: data.totalSizeFormatted || formatFileSize(data.totalSize),
            filesThisMonth: data.filesThisMonth,
            filesThisWeek: data.filesThisWeek,
            filesToday: data.filesToday,
            averageFileSize: data.averageFileSize,
            recentActivity: data.recentActivity || [],
            trends: data.trends || {},
          });
          setRecentActivity(data.recentActivity || []);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Fallback to empty data
        setStats({
          totalFiles: 0,
          totalSize: 0,
          totalSizeFormatted: '0 B',
          filesThisMonth: 0,
          filesThisWeek: 0,
          filesToday: 0,
          averageFileSize: 0,
          recentActivity: [],
          trends: {},
        });
        setRecentActivity([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Failed to load dashboard data</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.email}
          </h1>
          <p className="text-gray-600 mt-1">
            Here's what's happening with your file uploads today.
          </p>
        </div>
        <div className="flex space-x-3">
          <Link to="/upload" className="btn-primary">
            <Upload className="w-4 h-4 mr-2" />
            Upload Files
          </Link>
          <Link to="/files" className="btn-secondary">
            <Files className="w-4 h-4 mr-2" />
            Browse Files
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Files */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Files</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalFiles}</p>
              </div>
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
                <Files className="w-6 h-6 text-brand-600" />
              </div>
            </div>
            {renderTrendIndicator(stats.trends?.totalFiles, 'last month')}
          </div>
        </div>

        {/* Storage Used */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Current Storage</p>
                <p className="text-2xl font-bold text-gray-900">{formatFileSize(stats.totalSize)}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-gray-600">Temporary staging before DB restore</span>
            </div>
            {renderTrendIndicator(stats.trends?.storage, 'last month')}
          </div>
        </div>

        {/* Files This Week */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900">{stats.filesThisWeek}</p>
              </div>
              <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-success-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <Clock className="w-4 h-4 text-gray-400 mr-1" />
              <span className="text-gray-600">{stats.filesToday} uploaded today</span>
            </div>
            {renderTrendIndicator(stats.trends?.weekly, 'last week')}
          </div>
        </div>

        {/* Average File Size */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg File Size</p>
                <p className="text-2xl font-bold text-gray-900">{formatFileSize(stats.averageFileSize)}</p>
              </div>
              <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-warning-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-gray-600">Based on {stats.totalFiles} files</span>
            </div>
            {renderTrendIndicator(stats.trends?.averageFileSize, 'last month')}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Feed */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <Link to="/files" className="text-sm text-brand-600 hover:text-brand-700 flex items-center">
                View all
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {recentActivity.length > 0 ? recentActivity.map((item, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    item.action === 'upload' 
                      ? 'bg-brand-100 text-brand-600' 
                      : 'bg-success-100 text-success-600'
                  }`}>
                    {item.action === 'upload' ? (
                      <Upload className="w-4 h-4" />
                    ) : (
                      <ArrowUpRight className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {item.action === 'upload' ? 'Uploaded' : 'Downloaded'} {item.filename}
                    </p>
                    <p className="text-sm text-gray-600">
                      {item.userEmail} • {formatRelativeTime(item.timestamp)}
                      {item.compressionSaved && <span> • {item.compressionSaved} saved</span>}
                    </p>
                  </div>
                </div>
              )) : (
                <div className="text-center text-gray-500 py-4">
                  No recent activity
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <Link
                to="/upload"
                className="flex items-center p-4 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors duration-200 group"
              >
                <div className="w-10 h-10 bg-brand-500 rounded-lg flex items-center justify-center">
                  <Upload className="w-5 h-5 text-white" />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-sm font-medium text-gray-900 group-hover:text-brand-700">
                    Upload New Files
                  </h3>
                  <p className="text-sm text-gray-600">
                    Drag and drop files up to 10GB
                  </p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-brand-600" />
              </Link>

              <Link
                to="/files"
                className="flex items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 group"
              >
                <div className="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center">
                  <Files className="w-5 h-5 text-white" />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-sm font-medium text-gray-900">
                    Browse File History
                  </h3>
                  <p className="text-sm text-gray-600">
                    View and manage uploaded files
                  </p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              </Link>

              {user?.isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200 group"
                >
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-900 group-hover:text-purple-700">
                      System Administration
                    </h3>
                    <p className="text-sm text-gray-600">
                      Manage users and system settings
                    </p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};